import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

interface HomeworkItem {
  id: string;
  trainingLevel: 'beginner' | 'intermediate' | 'advanced' | 'pro';
  title: string;
  description: string;
  practiceAtHome: boolean;
  requiresCourt: boolean;
}

interface HomeworkAssignment {
  id: string;
  title: string;
  description: string;
  dueDate?: string | null;
  status: 'assigned' | 'submitted' | 'reviewed';
  parentFeedback?: string | null;
  assignedAt: string;
}

interface TrainingHomeworkPanelProps {
  playerId: string;
  coachId?: string;
  selectedSport: string;
  trainingLevel?: 'beginner' | 'intermediate' | 'advanced' | 'pro';
  canAssign?: boolean;
  canSubmitFeedback?: boolean;
}

const TrainingHomeworkPanel: React.FC<TrainingHomeworkPanelProps> = ({
  playerId,
  coachId,
  selectedSport,
  trainingLevel = 'beginner',
  canAssign = false,
  canSubmitFeedback = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [selectedLevel, setSelectedLevel] = useState<TrainingLevel>(trainingLevel);
  const [items, setItems] = useState<HomeworkItem[]>([]);
  const [assignments, setAssignments] = useState<HomeworkAssignment[]>([]);
  const [selectedItemId, setSelectedItemId] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [feedbackDrafts, setFeedbackDrafts] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const loadData = async () => {
    if (!playerId) return;
    try {
      setLoading(true);
      setError(null);
      const itemsParams = new URLSearchParams({
        sport: selectedSport || 'Badminton',
        trainingLevel: selectedLevel,
      });
      const assignmentsParams = new URLSearchParams({
        playerId,
        sport: selectedSport || 'Badminton',
        trainingLevel: selectedLevel,
      });
      if (coachId) assignmentsParams.set('coachId', coachId);

      const [itemsRes, assignmentsRes] = await Promise.all([
        fetch(`/api/admin/homework-items?${itemsParams.toString()}`),
        fetch(`/api/homework/assignments?${assignmentsParams.toString()}`),
      ]);
      const [itemsJson, assignmentsJson] = await Promise.all([itemsRes.json(), assignmentsRes.json()]);

      if (itemsJson.success) {
        setItems(itemsJson.data || []);
      } else {
        setItems([]);
      }
      if (assignmentsJson.success) {
        setAssignments(assignmentsJson.data || []);
      } else {
        setAssignments([]);
      }
    } catch (e: any) {
      setError(e.message || 'Failed to load homework data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadData();
  }, [playerId, coachId, selectedSport, selectedLevel]);

  const selectedItem = useMemo(
    () => items.find((item) => item.id === selectedItemId),
    [items, selectedItemId]
  );

  const handleAssign = async () => {
    if (!playerId || !coachId || !selectedItemId) return;
    try {
      setError(null);
      setSuccess(null);
      const response = await fetch('/api/homework/assignments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          playerId,
          coachId,
          sport: selectedSport || 'Badminton',
          trainingLevel: selectedLevel,
          homeworkItemId: selectedItemId,
          dueDate: dueDate || null,
        }),
      });
      const result = await response.json();
      if (!result.success) {
        setError(result.message || 'Failed to assign homework');
        return;
      }
      setSuccess('Homework assigned successfully');
      setSelectedItemId('');
      setDueDate('');
      await loadData();
    } catch (e: any) {
      setError(e.message || 'Failed to assign homework');
    }
  };

  const handleSubmitFeedback = async (assignmentId: string) => {
    const feedback = feedbackDrafts[assignmentId] || '';
    try {
      setError(null);
      const response = await fetch(`/api/homework/assignments/${assignmentId}/feedback`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ parentFeedback: feedback }),
      });
      const result = await response.json();
      if (!result.success) {
        setError(result.message || 'Failed to submit feedback');
        return;
      }
      setSuccess('Feedback submitted');
      await loadData();
    } catch (e: any) {
      setError(e.message || 'Failed to submit feedback');
    }
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
        Homework
      </Typography>
      <Box sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Training Level</InputLabel>
          <Select
            value={selectedLevel}
            label="Training Level"
            onChange={(e) => setSelectedLevel(e.target.value as TrainingLevel)}
          >
            <MenuItem value="beginner">Beginner</MenuItem>
            <MenuItem value="intermediate">Intermediate</MenuItem>
            <MenuItem value="advanced">Advanced</MenuItem>
            <MenuItem value="pro">Pro</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {canAssign && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              Assign Homework
            </Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <FormControl size="small" sx={{ minWidth: 280 }}>
                <InputLabel>Homework Item</InputLabel>
                <Select
                  value={selectedItemId}
                  label="Homework Item"
                  onChange={(e) => setSelectedItemId(String(e.target.value))}
                >
                  {items.map((item) => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                size="small"
                label="Due Date"
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <Button
                variant="contained"
                onClick={handleAssign}
                disabled={!selectedItemId || loading}
              >
                Assign
              </Button>
            </Stack>
            {selectedItem && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {selectedItem.description}
              </Typography>
            )}
          </CardContent>
        </Card>
      )}

      <Stack spacing={1.5}>
        {assignments.length === 0 ? (
          <Alert severity="info">No homework assigned for this level yet.</Alert>
        ) : (
          assignments.map((assignment) => (
            <Card key={assignment.id}>
              <CardContent>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>
                    {assignment.title}
                  </Typography>
                  <Chip label={assignment.status} size="small" />
                </Stack>
                {assignment.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {assignment.description}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Assigned: {new Date(assignment.assignedAt).toLocaleDateString()}
                  {assignment.dueDate ? ` | Due: ${assignment.dueDate}` : ''}
                </Typography>

                {canSubmitFeedback && (
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Parent / Player Feedback"
                      value={feedbackDrafts[assignment.id] ?? assignment.parentFeedback ?? ''}
                      onChange={(e) =>
                        setFeedbackDrafts((prev) => ({ ...prev, [assignment.id]: e.target.value }))
                      }
                    />
                    <Button variant="outlined" onClick={() => handleSubmitFeedback(assignment.id)}>
                      Update Feedback
                    </Button>
                  </Stack>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </Stack>
    </Box>
  );
};

export default TrainingHomeworkPanel;
